import { EJobPriority, EJobStatus } from '@quesmed/types/models';
import { CiMemoPad } from 'react-icons/ci';
import {
  FaCheckCircle,
  FaFireAlt,
  FaLock,
  FaMoneyBill,
  FaStarHalfAlt,
  FaTelegramPlane,
} from 'react-icons/fa';
import { FaBan, FaCheck, FaRocket } from 'react-icons/fa6';
import { FiUnlock } from 'react-icons/fi';
import { GrTest } from 'react-icons/gr';
import { ImHourGlass } from 'react-icons/im';
import type { IconType } from 'react-icons/lib';
import { RiCircleFill, RiMailSendFill } from 'react-icons/ri';
import { RxCross2 } from 'react-icons/rx';

export const jobStatusColorMap: Record<
  EJobStatus,
  { className: string; icon: { type: IconType; className: string } }
> = {
  [EJobStatus.ALL]: {
    className: 'bg-default-100 text-default-500 border border-default-200',
    icon: { type: CiMemoPad, className: 'size-3.5 text-default-500' },
  },
  [EJobStatus.IDLE]: {
    className: 'bg-primary-200 text-primary-800 border border-primary-400',
    icon: { type: ImHourGlass, className: 'size-3.5 text-primary-800' },
  },
  [EJobStatus.ONGOING]: {
    className: 'bg-warning-50 text-warning-500 border border-warning-200',
    icon: { type: FaFireAlt, className: 'size-3.5 text-warning-500' },
  },
  [EJobStatus.SUBMITTED]: {
    className: 'bg-secondary-100 text-secondary-600 border border-secondary-200',
    icon: { type: RiMailSendFill, className: 'size-3.5 text-secondary-500' },
  },
  [EJobStatus.REJECTED]: {
    className: 'bg-danger-50 text-danger-500 border border-danger-200',
    icon: { type: RxCross2, className: 'size-3.5 text-danger-500' },
  },
  [EJobStatus.APPROVED]: {
    className: 'bg-success-50 text-success-500 border border-success-200',
    icon: { type: FaCheck, className: 'size-3.5 text-success-500' },
  },
  [EJobStatus.TESTING]: {
    className: 'bg-pink-100 dark:bg-pink-950 text-pink-500 border border-pink-200 dark:border-pink-800',
    icon: { type: GrTest, className: 'size-3.5 text-pink-500' },
  },
  [EJobStatus.PARTIAL_TRIALING]: {
    className: 'bg-lime-100 dark:bg-lime-950 text-lime-600 border border-lime-300 dark:border-lime-800',
    icon: { type: FaStarHalfAlt, className: 'size-3.5 text-lime-500' },
  },
  [EJobStatus.PUBLISHED]: {
    className: 'bg-cyan-100 dark:bg-cyan-950 text-cyan-500 border border-cyan-200 dark:border-cyan-800',
    icon: { type: FaRocket, className: 'size-3.5 text-cyan-500' },
  },
  [EJobStatus.CLOSED]: {
    className: 'bg-rose-50 dark:bg-rose-950 text-rose-500 border border-rose-200 dark:border-rose-800',
    icon: { type: FaLock, className: 'size-3.5 text-rose-500' },
  },
  [EJobStatus.PAID]: {
    className:
      'bg-emerald-100 dark:bg-emerald-950 text-emerald-600 border border-emerald-200 dark:border-emerald-800',
    icon: { type: FaMoneyBill, className: 'size-3.5 text-emerald-600' },
  },
};

export const jobPriorityColorMap: Record<
  EJobPriority,
  { className: string; icon: { type: IconType; className: string } }
> = {
  [EJobPriority.LOW]: {
    className: 'bg-success-50 text-success-500 border border-success-200',
    icon: { type: RiCircleFill, className: 'size-3.5 text-success-500 mr-1' },
  },
  [EJobPriority.MEDIUM]: {
    className: 'bg-warning-50 text-warning-500 border border-warning-200',
    icon: { type: RiCircleFill, className: 'size-3.5 text-warning-500 mr-1' },
  },
  [EJobPriority.HIGH]: {
    className: 'bg-danger-50 text-danger-500 border border-danger-200',
    icon: { type: RiCircleFill, className: 'size-3.5 text-danger-500 mr-1' },
  },
};

export const iconClasses = 'text-default-500 size-3.5 pointer-events-none flex-shrink-0';

export const JOB_STATUS_MESSAGES: Record<
  Exclude<EJobStatus, EJobStatus.ALL>,
  { loading: string; success: string }
> = {
  [EJobStatus.IDLE]: {
    loading: 'Idling the Job...',
    success: 'Successfully Idled the Job',
  },
  [EJobStatus.APPROVED]: {
    loading: 'Approving the Job...',
    success: 'Successfully Approved the Job',
  },
  [EJobStatus.REJECTED]: {
    loading: 'Rejecting the Job...',
    success: 'Successfully Rejected the Job',
  },
  [EJobStatus.PUBLISHED]: {
    loading: 'Publishing the Job...',
    success: 'Successfully Published the Job',
  },
  [EJobStatus.TESTING]: {
    loading: 'Moving to Testing...',
    success: 'Successfully Moved to Testing',
  },
  [EJobStatus.ONGOING]: {
    loading: 'Moving to Ongoing...',
    success: 'Successfully Moved to Ongoing',
  },
  [EJobStatus.SUBMITTED]: {
    loading: 'Submitting the Job...',
    success: 'Successfully Submitted the Job',
  },
  [EJobStatus.PARTIAL_TRIALING]: {
    loading: 'Moving to Partial Trial...',
    success: 'Successfully Moved to Partial Trial',
  },
  [EJobStatus.CLOSED]: {
    loading: 'Closing the Job...',
    success: 'Successfully Closed the Job',
  },
  [EJobStatus.PAID]: {
    loading: 'Marking as Paid...',
    success: 'Successfully Marked Job as Paid',
  },
};

export type JobActionConfig = {
  title: string;
  description: string;
  color: 'success' | 'danger' | 'secondary' | 'warning' | 'default';
  icon: React.ComponentType<{ className?: string }>;
  type: 'warn' | 'danger' | 'success' | 'info' | 'default';
  status: EJobStatus;
  className?: string;
};

export const JOB_ACTION_CONFIGS: Partial<Record<EJobStatus, JobActionConfig>> = {
  [EJobStatus.SUBMITTED]: {
    title: 'Submit for Review',
    description:
      'Are you sure you want to submit this job for review? You can not edit this job after submission.',
    color: 'success',
    icon: FaTelegramPlane,
    status: EJobStatus.SUBMITTED,
    type: 'warn',
  },
  [EJobStatus.APPROVED]: {
    title: 'Approve Job',
    description: 'Are you sure you want to approve this job?',
    color: 'success',
    icon: FaCheckCircle,
    status: EJobStatus.APPROVED,
    type: 'warn',
  },
  [EJobStatus.REJECTED]: {
    title: 'Reject Job',
    description: 'Are you sure you want to reject this job? IT will be open for editing again by writer.',
    color: 'danger',
    icon: FaBan,
    status: EJobStatus.REJECTED,
    type: 'danger',
  },
  [EJobStatus.TESTING]: {
    title: 'Move to Testing',
    description: 'Are you sure you want to move this job to testing?',
    color: 'default',
    icon: GrTest,
    status: EJobStatus.TESTING,
    type: 'default',
    className: 'text-foreground',
  },
  [EJobStatus.ONGOING]: {
    title: 'Unlock Job',
    description:
      'Are you sure you want to unlock this job? It will change all records status to `DRAFT` i.e open for edits again and also change batch status to `ONGOING`. The job cannot be reviewed until it is submitted again. You could instead unlock only few records by selecting them and clicking the unlock button.',
    color: 'warning',
    icon: FiUnlock,
    className: 'text-black',
    status: EJobStatus.ONGOING,
    type: 'warn',
  },
  [EJobStatus.PUBLISHED]: {
    title: 'Publish Job',
    description: 'Are you sure you want to publish this job?',
    color: 'secondary',
    icon: FaRocket,
    status: EJobStatus.PUBLISHED,
    type: 'info',
    className: 'text-white',
  },
  [EJobStatus.PAID]: {
    title: 'Mark as Paid',
    description: 'Are you sure you want to mark this job as paid?',
    color: 'success',
    icon: FaMoneyBill,
    status: EJobStatus.PAID,
    type: 'warn',
  },
};
